import { useContext, useEffect, useRef, type ReactNode } from "react";
import { AuthContext } from "react-oauth2-code-pkce";

import { AppContext } from "../../app/Application.context";
import { getCurrentTenant, setActiveTenantCookie } from "../../auth/utils";
import { usePrefetchOnLogin } from "../../hooks/usePrefetchOnLogin";
import { setAuthToken } from "../../lib/apiClient";
import type { User } from "../../types";

interface OAuthBridgeProps {
  children: ReactNode;
}

interface DecodedToken {
  sub?: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  // Standard OIDC role claims
  roles?: string[];
  groups?: string[];
  // Keycloak-style nested roles
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
  [key: string]: unknown;
}

function extractRoles(tokenData: DecodedToken): string[] {
  // Check common role claim locations in order of specificity.
  if (Array.isArray(tokenData.roles)) return tokenData.roles as string[];
  if (Array.isArray(tokenData.groups)) return tokenData.groups as string[];
  if (Array.isArray(tokenData.realm_access?.roles)) return tokenData.realm_access!.roles as string[];
  return [];
}

function extractUserFromToken(tokenData: DecodedToken): User {
  const id = tokenData.sub ?? "";
  const email = tokenData.email ?? tokenData.preferred_username ?? "";

  let name = tokenData.name ?? "";
  if (!name && tokenData.given_name) {
    name = tokenData.family_name
      ? `${tokenData.given_name} ${tokenData.family_name}`
      : (tokenData.given_name as string);
  }
  if (!name) name = email.split("@")[0] || "User";

  const avatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;

  return { id, name, username: email, avatar, roles: extractRoles(tokenData) };
}

export function OAuthBridge({ children }: OAuthBridgeProps): ReactNode {
  const authContext = useContext(AuthContext);
  const appContext = useContext(AppContext);

  usePrefetchOnLogin();
  const previousTokenRef = useRef<string | null>(null);
  const hasDispatchedLoadingRef = useRef(false);

  useEffect(() => {
    if (!appContext) return;

    const { dispatch, auth } = appContext;
    const { token, idTokenData, loginInProgress, error } = authContext;

    if (loginInProgress && !auth.isLoading && !hasDispatchedLoadingRef.current) {
      hasDispatchedLoadingRef.current = true;
      dispatch({ type: "AUTH_LOADING" });
      return;
    }

    if (!loginInProgress) {
      hasDispatchedLoadingRef.current = false;
    }

    if (error && !auth.message) {
      dispatch({ type: "AUTH_ERROR", payload: { error } });
      return;
    }

    const currentToken = token ?? null;
    if (currentToken === previousTokenRef.current) return;
    previousTokenRef.current = currentToken;

    if (token && idTokenData) {
      // Install the Axios interceptor BEFORE dispatch so that any subscriber
      // re-rendered by the AUTH_LOGIN action (e.g. `useMe`) sees a fully
      // authenticated apiClient on its first fetch. AppProvider's mirrored
      // effect would otherwise run after child effects on the same commit
      // and produce a 401 on the first request.
      setAuthToken(token);

      // Set active tenant cookie for single-client session enforcement.
      // Cookie is shared across all tenant subdomains via root domain.
      const tenant = getCurrentTenant();
      if (tenant) {
        setActiveTenantCookie(tenant);
      }

      const user = extractUserFromToken(idTokenData as DecodedToken);
      dispatch({ type: "AUTH_LOGIN", payload: { user, token } });
    } else if (!token && auth.isAuthenticated) {
      setAuthToken(null);
      dispatch({ type: "AUTH_LOGOUT" });
    }
  }, [authContext, appContext]);

  return children;
}
